import { useEffect, useState } from 'react'
import { getTrustLedgerAdditionalInfo } from 'services'
import { confirm, formatDate, isBeforeOrEqualToday } from 'utils'

/**
 * A payoff is "active" once its Good through Date has arrived: the amount quoted on the payoff
 * statement is what the borrower is paying, so anything posted to the loan after that date
 * silently invalidates the quote. `payoffDate` is the servicing Payoff screen's Good through
 * Date (trust-ledger additional info).
 */
export const isActivePayoff = (payoffDate?: string | null) => !!payoffDate && isBeforeOrEqualToday(payoffDate)

/**
 * The warning shown before entering a transaction on a loan with an active payoff. Resolves
 * true when it is safe to continue (no active payoff, or the user chose to continue anyway).
 */
export const confirmActivePayoff = async (payoffDate?: string | null) => {
  if (!isActivePayoff(payoffDate)) return true
  const message = `There's an active payoff with a good through date of ${formatDate(
    payoffDate as string,
  )}.\nWould you like to continue?`
  return await confirm(message)
}

/**
 * Loads the loan's Good through Date once and returns a guard to run before entering a
 * transaction (payment, charge, ledger entry, voucher). A failed lookup never blocks the
 * user - the guard just resolves true.
 *
 * The request is loan-scoped through the shared Api loan-number header, so only call this
 * from a screen that is already pointed at the loan.
 */
export const usePayoffGuard = () => {
  const [payoffDate, setPayoffDate] = useState('')

  useEffect(() => {
    let cancelled = false
    getTrustLedgerAdditionalInfo()
      .then((data: Record<string, any>) => {
        if (!cancelled) setPayoffDate(data?.payoffDate || '')
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  return () => confirmActivePayoff(payoffDate)
}
